/**
 * Global search (screenshots/113–114, §2.20; §3.4 row 4). Pure: the response shape of GET /api/dashboard/search
 * (worker/src/search/query.js), grouping and ranking for the palette, highlighting, and the text each surface
 * shows for a result. The palette and the results page render from these.
 */
import type { FollowType, Person } from "../types";
import { clock } from "../lib/format";
import { highlightParts } from "../lib/followText";

export type SearchKind = "legislation" | "meeting" | "person" | "organization" | "filing";
/** What the result is inside its kind (worker/src/search/docs.js). */
export type SearchSub = "matter" | "agenda" | "moment" | "mention" | "passage" | "summary" | "document" | "video" | "person" | "client" | "filing";
/** The tabs / scope chips: all kinds, or one. */
export type Scope = "all" | SearchKind;
/** "In your follows" or everything. */
export type SearchScope = "all" | "follows";

export interface SearchFollow { type: FollowType | "organization"; refId?: string; keyword?: string; label: string }
export interface SearchResult {
  kind: SearchKind;
  sub: SearchSub;
  id: string;
  title: string;                // bill / agenda item title, topic, person, organization, filing text
  snippet?: string;             // the matched passage
  hl?: [number, number][];      // hit ranges in `snippet` (the index's stemmed matches)
  href: string;
  score: number;
  followed?: boolean;
  follow?: SearchFollow;        // what ⇧↵ follows
  recordId?: string;
  meetingId?: string;
  at?: number;                  // second in the meeting video ("open at moment")
  personId?: string;
  context?: string;             // "City Council, Sep 29"
  status?: string;              // "Passed", "Lobbying client", "Alderperson"
  where?: string;               // committee, "Ward 36"
  upcoming?: string;            // "City Council Oct 14"
  client?: string;              // filings
  period?: string;              // filings: "Jun 30, 2026"
  filings?: number;             // organizations
  lobbied?: number;
}
export interface SearchResponse {
  q: string; scope: SearchScope; kind: Scope;
  results: SearchResult[];
  counts: Record<Scope, number>;
  total: number; tookMs: number;
  people: Record<string, Person>;
  bodies?: string[];
}
/** A palette row: a result, or "Follow “q” as a keyword". */
export type PaletteItem = SearchResult | { kind: "action"; id: "follow"; title: string; meta: string; q: string };

export const SCOPES: { id: Scope; label: string }[] = [
  { id: "all", label: "All" }, { id: "legislation", label: "Legislation" }, { id: "meeting", label: "Meetings" },
  { id: "person", label: "People" }, { id: "organization", label: "Organizations" }, { id: "filing", label: "Filings" },
];
/** The results page's tabs (114). */
export const PAGE_TABS: { id: Scope; label: string }[] = SCOPES.map((s) => (s.id === "meeting" ? { ...s, label: "Meetings & transcripts" } : s));

const GROUP: Record<SearchKind | "action", string> = { legislation: "Legislation", meeting: "Meetings", person: "People", organization: "Organizations & filings", filing: "Organizations & filings", action: "Do something" };
/** Organizations before their filings in the shared group (113). */
const KIND_RANK: Record<SearchKind | "action", number> = { legislation: 0, meeting: 0, person: 0, organization: 0, filing: 1, action: 2 };
const ORDER = ["Legislation", "Meetings", "People", "Organizations & filings", "Do something"];

/** Always offered for a query (113 "Do something"). */
export const actionsFor = (q: string): PaletteItem[] => q.trim().length < 2 ? [] : [
  { kind: "action", id: "follow", title: `Follow “${q.trim()}” as a keyword`, meta: "Matches in agendas, transcripts and filings", q: q.trim() },
];

/** Followed first, then by score; at most `per` per group; actions last (§2.20). */
export function groupResults(rs: PaletteItem[], scope: Scope, per = 3): { label: string; items: PaletteItem[] }[] {
  const pool = rs.filter((r) => scope === "all" || r.kind === scope || r.kind === "action")
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (a.r.kind === "action" || b.r.kind === "action" ? 0 : Number(!!(b.r as SearchResult).followed) - Number(!!(a.r as SearchResult).followed)
      || KIND_RANK[a.r.kind] - KIND_RANK[b.r.kind] || (b.r as SearchResult).score - (a.r as SearchResult).score) || a.i - b.i)
    .map((x) => x.r);
  const g = new Map<string, PaletteItem[]>();
  for (const r of pool) { const k = GROUP[r.kind]; const xs = g.get(k) ?? []; if (xs.length < per || r.kind === "action") g.set(k, [...xs, r]); }
  return ORDER.filter((k) => g.has(k)).map((label) => ({ label, items: g.get(label)! }));
}

/** Tab / Shift+Tab in the palette. */
export const nextScope = (s: Scope, back = false) => { const i = SCOPES.findIndex((x) => x.id === s); return SCOPES[(i + (back ? -1 : 1) + SCOPES.length) % SCOPES.length].id; };

/** Plain and highlighted runs: the index's own hit ranges when it sent them, otherwise the query's words. */
export function highlight(text: string, q: string, hl?: [number, number][]): { text: string; hit: boolean }[] {
  if (!hl?.length) return highlightParts(text, q);
  const out: { text: string; hit: boolean }[] = [];
  let last = 0;
  for (const [a, b] of [...hl].sort((x, y) => x[0] - y[0])) {
    if (a < last || b > text.length) continue;
    if (a > last) out.push({ text: text.slice(last, a), hit: false });
    out.push({ text: text.slice(a, b), hit: true });
    last = b;
  }
  if (last < text.length) out.push({ text: text.slice(last), hit: false });
  return out;
}

const dot = (...xs: (string | false | null | undefined)[]) => xs.filter(Boolean).join(" · ");
/** Shortened on a word boundary: "Protecting Renters Ordinance …". */
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).replace(/\s+\S*$/, "")} …` : s);
/** "City Council, Sep 29" → "City Council · Sep 29" (113's row titles). */
const ctxDot = (c?: string) => (c ?? "").replace(/, (?=[A-Z][a-z]{2} \d)/, " · ");
const isMoment = (r: SearchResult) => r.sub === "moment" || r.sub === "mention" || r.sub === "passage";

/** A palette row's two lines (113). `speaker` is the moment's speaker, when known and visible. */
export function paletteText(r: SearchResult, speaker?: Person): { title: string; meta: string; metaHl?: boolean } {
  switch (r.sub) {
    case "matter": return { title: r.title, meta: dot(r.recordId, r.upcoming ?? r.status) };
    case "agenda": return { title: dot(ctxDot(r.context), r.at != null && `agenda item at ${clock(r.at)}`),
      meta: dot(`${r.recordId} on the agenda as “${cut(r.title, 32)}”${r.status ? `, ${r.status.toLowerCase()}` : ""}`, r.at != null && "jump to the moment") };
    case "moment": case "mention": case "passage":
      return { title: dot(speaker?.name, ctxDot(r.context), r.at != null && clock(r.at)), meta: r.snippet ?? r.title, metaHl: true };
    case "summary": return { title: ctxDot(r.context) || r.title, meta: r.snippet ?? "", metaHl: true };
    case "document": return { title: r.title, meta: dot(r.recordId, r.status, r.context) };
    case "video": return { title: r.title, meta: dot(ctxDot(r.context), r.status) };
    case "person": return { title: r.title, meta: dot(r.status, r.where) };
    case "client": return { title: r.title, meta: dot(r.status, r.recordId && `lobbied ${r.recordId}`) };
    case "filing": return { title: `${r.client} filing: “${r.title}”`, meta: dot("Board of Ethics", r.period && `quarter ending ${r.period}`) };
  }
}

/** A results-page row (114): eyebrow, title, snippet, facts. */
export function pageText(r: SearchResult, speaker?: Person): { label: string; title: string; snippet?: string; meta: string } {
  switch (r.sub) {
    case "matter": return { label: dot("Legislation", r.upcoming && `up for a vote ${r.upcoming.replace(/^.* (?=[A-Z][a-z]{2} \d)/, "")}`), title: r.title, snippet: r.snippet,
      meta: dot(r.recordId, r.where || r.status, r.followed && "you follow this") };
    case "agenda": return { label: dot("Meeting moment", r.context, r.at != null && clock(r.at)), title: r.title, meta: dot(r.recordId, r.status, r.at != null && "jump to the moment in the video") };
    case "moment": case "mention": case "passage":
      return { label: dot("Meeting moment", r.context, r.at != null && clock(r.at)), title: speaker?.name ?? r.title, snippet: r.snippet, meta: dot(r.sub === "moment" && speaker ? r.title : null, r.at != null && "jump to the moment in the video") };
    case "summary": return { label: dot("Meeting", r.context), title: r.title, snippet: r.snippet, meta: "" };
    case "document": return { label: dot(r.kind === "legislation" ? "Legislation" : "Meeting", r.status), title: r.title, snippet: r.snippet, meta: dot(r.recordId, r.context) };
    case "video": return { label: dot(r.context, r.status), title: r.title, snippet: r.snippet, meta: dot(r.recordId) };
    case "person": return { label: dot("Person", r.status), title: r.title, snippet: r.snippet, meta: dot(r.where, r.followed && "you follow this") };
    case "client": return { label: "Organization · lobbying client", title: r.title, snippet: r.snippet,
      meta: dot(r.lobbied != null && `${r.lobbied} items lobbied`, r.filings != null && `${r.filings} filings`, r.followed && "you follow this") };
    case "filing": return { label: "Lobbying filing · Board of Ethics", title: `${r.client}: “${r.title}”`, meta: dot(r.period && `Quarter ending ${r.period}`, r.recordId) };
  }
}

/** Where ↵ goes (a moment opens the meeting at its second). ⌘↵ opens it in a new tab. */
export const hrefOf = (r: SearchResult) => r.href || null;
export const opensAtMoment = (r?: PaletteItem) => !!r && r.kind !== "action" && (isMoment(r) || r.sub === "agenda") && r.at != null;

/** The API URL for a query. */
export function searchUrl(q: string, o: { scope?: SearchScope; kind?: Scope; per?: number; offset?: number; limit?: number; time?: string; body?: string; ward?: string; deep?: boolean; facets?: boolean } = {}) {
  const p = new URLSearchParams({ q: q.trim() });
  if (o.scope && o.scope !== "all") p.set("scope", o.scope);
  if (o.kind && o.kind !== "all") p.set("kind", o.kind);
  for (const k of ["per", "offset", "limit"] as const) if (o[k]) p.set(k, String(o[k]));
  for (const k of ["time", "body", "ward"] as const) if (o[k]) p.set(k, o[k]!);
  if (o.deep) p.set("deep", "1");
  if (o.facets) p.set("facets", "1");
  return `/api/dashboard/search?${p}`;
}
